import React, { useState, useRef, useEffect } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import config from "../../../../../config";
import { fetchClusterInfo } from "layouts/cluster/data/fetchClusterInfo";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

const MapSelect = ({ selectedPoint, setSelectedPoint }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));

  // Predefined points (longitude, latitude)
  const predefinedPoints = [
    { id: 1, coordinates: [28.081913, 36.402218], name: "Cluster 1" },
    { id: 2, coordinates: [28.091913, 36.412218], name: "Cluster 2" },
    { id: 3, coordinates: [28.071913, 36.392218], name: "Cluster 3" },
    { id: 4, coordinates: [28.101913, 36.382218], name: "Cluster 4" },
  ];

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: mapStyle,
        center,
        zoom,
        accessToken: token,
      });

      mapRef.current.addControl(new mapboxgl.NavigationControl());

      // Add geolocate control to the map
      const currentGeolocation = new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true,
        },
        trackUserLocation: false,
        showUserHeading: false,
        showAccuracyCircle: false,
      });

      mapRef.current.addControl(currentGeolocation);

      // When the control gets a position, override the view (optional)
      currentGeolocation.on("geolocate", (e) => {
        const { longitude, latitude } = e.coords;
        mapRef.current.flyTo({
          center: [longitude, latitude],
          zoom: 15, // your preferred zoom
          essential: true,
        });
      });

      // Wait for the map to load before adding sources and layers
      mapRef.current.on("load", async () => {
        const clusterData = await fetchClusterInfo();

        mapRef.current.getCanvas().style.cursor = "pointer";

        // Add the source
        mapRef.current.addSource("clusters", {
          type: "geojson",
          data: clusterData,
        });

        // Add the layer
        mapRef.current.addLayer({
          id: "clusters-layer",
          type: "fill",
          source: "clusters",
          paint: {
            "fill-color": "#ff6600",
            "fill-opacity": 0.5,
          },
        });

        // Optional: Add an outline
        mapRef.current.addLayer({
          id: "clusters-outline",
          type: "line",
          source: "clusters",
          paint: {
            "line-color": "#ff0000",
            "line-width": 2,
          },
        });

        // Add hover popup
        const popupHover = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
        });

        mapRef.current.on("mousemove", "clusters-layer", (e) => {
          const props = e.features[0].properties;

          popupHover
            .setLngLat(e.lngLat)
            .setHTML(
              `
                            <strong>${props.name}</strong><br/>
                            Cluster ID: ${props.cluster_id}<br/>
                            Accidents: ${props.accidents}<br/>
                            Indications: ${props.indications}<br/>
                            Interventions: ${props.interventions}<br/>
                            Last Updated: ${props.latestUpdate}
                        `
            )
            .addTo(mapRef.current);
        });

        mapRef.current.on("mouseleave", "clusters-layer", () => {
          mapRef.current.getCanvas().style.cursor = "";
          popupHover.remove();
        });

        // Add a click event to select a point
        mapRef.current.on("click", (e) => {
          const { lng, lat } = e.lngLat; // Get the coordinates of the clicked point
          setSelectedPoint([lng, lat]); // Update the state with selected coordinates
          // Create a popup to display the selected point
          const popup = popupRef.current
            .setLngLat([lng, lat])
            .setHTML(
              `<h3>Επιλεγμένο Σημείο</h3><p>Longitude: ${lng}<br />Latitude: ${lat}</p>`
            )
            .addTo(mapRef.current)
            .on("close", () => {
              setSelectedPoint(null); //  Reset state when popup is closed
            });

          // Fly to the point
          mapRef.current.flyTo({
            center: [lng, lat],
            essential: true,
            zoom: 12,
          });
        });

        // // Change the cursor to a pointer when hovering over points
        // mapRef.current.on('mouseenter', 'points', () => {
        //     mapRef.current.getCanvas().style.cursor = 'pointer';
        // });

        // // Change it back to default when leaving
        // mapRef.current.on('mouseleave', 'points', () => {
        //     mapRef.current.getCanvas().style.cursor = '';
        // });
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
  }, [darkMode]);

  // change region
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    popupRef.current.remove();
    map.flyTo({ center, zoom, essential: true });
  }, [center, zoom]);

useEffect(() => {
  const map = mapRef.current;
  if (!selectedPoint || !map) return;

  const showPoint = () => {
    const [lng, lat] = selectedPoint;

    popupRef.current
      .setLngLat([lng, lat])
      .setHTML(
        `<h3>Επιλεγμένο Σημείο</h3>
         <p>Longitude: ${Number(lng).toFixed(6)}<br/>
         Latitude: ${Number(lat).toFixed(6)}</p>`
      )
      .addTo(map);

    map.flyTo({
      center: [lng, lat],
      essential: true,
      zoom: 12,
    });
  };

  // If style isn't loaded yet, wait for load
  if (!map.isStyleLoaded()) {
    map.once("load", showPoint);
    return;
  }

  showPoint();
}, [selectedPoint]);


  return (
    <div
      ref={mapContainerRef}
      style={{
        width: "100%",
        height: "100%",
      }}
    />
  );
};

export default MapSelect;
